/**
 * Stundenplan Mo-Fr (F-22 Stufe 2). Jede Stunde ist ein Serientermin im
 * Kalender - Aenderungen dort erscheinen hier und umgekehrt.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, X } from 'lucide-react';

import { Input } from '@/ui/primitives/Input';
import { Button } from '@/ui/primitives/Button';
import { AreaDot } from '@/modules/areas/AreaDot';
import { useLinkNames } from '@/modules/links/linkNames';
import { createLesson, deleteLesson } from '@/db/repositories/school';
import {
  SCHULTAGE, TAG_NAMEN, stundenDerWoche, type Stunde, type StundenWoche,
} from '@/domain/school/timetable';
import { abWocheVon } from '@/domain/school/weeks';
import { today, weekday } from '@/lib/datetime';
import { cn } from '@/lib/utils/cn';
import type { SchoolSettings, Subject } from '@/domain/types';

export function Stundenplan({ settings, faecher, stunden }: {
  settings: SchoolSettings;
  faecher: Subject[];
  stunden: Stunde[];
}) {
  const heute = today();
  const aktuell = abWocheVon(heute, settings.aWocheAb);
  const [woche, setWoche] = useState<'A' | 'B'>(aktuell);
  const tage = stundenDerWoche(stunden, settings.abWochen ? woche : null);
  const heutigerTag = weekday(heute);
  const { subjects } = useLinkNames();

  return (
    <section aria-label="Stundenplan" className="flex flex-col gap-3 rounded-lg border border-line bg-surface px-4 py-3.5">
      <header className="flex flex-wrap items-center gap-2">
        <h2 className="flex-1 text-sm font-semibold">Stundenplan</h2>
        {settings.abWochen && (
          <div role="group" aria-label="Woche" className="flex gap-1 text-[12px]">
            {(['A', 'B'] as const).map((w) => (
              <button
                key={w}
                type="button"
                aria-pressed={woche === w}
                onClick={() => setWoche(w)}
                className={cn(
                  'rounded-full border px-2.5 py-0.5 transition-colors',
                  woche === w ? 'border-accent bg-accent/10 text-ink' : 'border-line text-ink-muted hover:text-ink',
                )}
              >
                {w}-Woche{w === aktuell ? ' (jetzt)' : ''}
              </button>
            ))}
          </div>
        )}
      </header>

      <div className="grid gap-2 sm:grid-cols-5">
        {SCHULTAGE.map((tag) => (
          <div key={tag} className={cn('flex min-w-0 flex-col gap-1.5 rounded-md p-1.5', tag === heutigerTag && woche === aktuell && 'bg-accent/[0.06]')}>
            <h3 className="text-[11px] font-medium uppercase tracking-wide text-ink-faint">{TAG_NAMEN[tag]}</h3>
            {tage.get(tag)!.length === 0 && <p className="text-[11px] text-ink-faint">–</p>}
            <ul className="flex flex-col gap-1.5">
              {tage.get(tag)!.map((s) => {
                const fach = subjects.get(s.subjectId);
                return (
                  <li key={s.eventId} className="group relative rounded border border-line bg-surface-raised px-2 py-1.5 text-[12px]">
                    <div className="flex items-center gap-1.5">
                      <AreaDot color={fach?.color ?? 'var(--color-line-strong)'} size="sm" />
                      <span className="min-w-0 flex-1 truncate font-medium">{fach?.name ?? 'Fach'}</span>
                    </div>
                    <p className="tabular-nums text-ink-faint">{s.von}–{s.bis}</p>
                    {(s.raum || s.woche !== 'jede') && (
                      <p className="truncate text-ink-faint">{[s.raum, s.woche !== 'jede' ? `nur ${s.woche}` : null].filter(Boolean).join(' · ')}</p>
                    )}
                    <button
                      type="button"
                      onClick={() => { if (window.confirm(`${fach?.name ?? 'Stunde'} am ${TAG_NAMEN[tag]} ${s.von} aus dem Stundenplan und Kalender entfernen? Kommt für 30 Tage in den Papierkorb.`)) void deleteLesson(s.eventId); }}
                      aria-label={`${fach?.name ?? 'Stunde'} ${TAG_NAMEN[tag]} ${s.von} entfernen`}
                      className="absolute right-1 top-1 grid size-5 place-items-center rounded text-ink-faint opacity-0 hover:text-danger focus:opacity-100 group-hover:opacity-100"
                    >
                      <X className="size-3" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {faecher.length > 0 && <NeueStundeForm faecher={faecher} abWochen={settings.abWochen} />}
      <p className="text-[11px] text-ink-faint">
        Jede Stunde ist ein wiederkehrender Termin im <Link to="/kalender" className="underline hover:text-ink">Kalender</Link>. Uhrzeit oder Raum lassen sich auch dort ändern.
      </p>
    </section>
  );
}

function NeueStundeForm({ faecher, abWochen }: { faecher: Subject[]; abWochen: boolean }) {
  const [fach, setFach] = useState('');
  const [tag, setTag] = useState(1);
  const [von, setVon] = useState('08:00');
  const [bis, setBis] = useState('08:45');
  const [raum, setRaum] = useState('');
  const [woche, setWoche] = useState<StundenWoche>('jede');
  const fachId = fach || faecher[0]!.id;
  const gueltig = von < bis;

  async function anlegen() {
    if (!gueltig) return;
    await createLesson({ subjectId: fachId, tag, von, bis, raum, woche });
    setRaum('');
  }

  const feld = 'h-9 rounded-md border border-line bg-surface px-2 text-sm';
  return (
    <div className="flex flex-wrap items-end gap-2 border-t border-line pt-2.5">
      <select value={fachId} onChange={(e) => setFach(e.target.value)} aria-label="Fach der Stunde" className={feld}>
        {faecher.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
      </select>
      <select value={tag} onChange={(e) => setTag(Number(e.target.value))} aria-label="Wochentag" className={feld}>
        {SCHULTAGE.map((t) => <option key={t} value={t}>{TAG_NAMEN[t]}</option>)}
      </select>
      <div className="w-32"><Input type="time" value={von} onChange={(e) => setVon(e.target.value)} aria-label="Beginn" className="h-9 text-sm" /></div>
      <div className="w-32"><Input type="time" value={bis} onChange={(e) => setBis(e.target.value)} aria-label="Ende" className="h-9 text-sm" /></div>
      <div className="w-28"><Input value={raum} onChange={(e) => setRaum(e.target.value)} placeholder="Raum" aria-label="Raum" className="h-9 text-sm" /></div>
      {abWochen && (
        <select value={woche} onChange={(e) => setWoche(e.target.value as StundenWoche)} aria-label="In welchen Wochen" className={feld}>
          <option value="jede">Jede Woche</option>
          <option value="A">Nur A-Woche</option>
          <option value="B">Nur B-Woche</option>
        </select>
      )}
      <Button size="sm" variant="primary" disabled={!gueltig} onClick={() => void anlegen()}>
        <Plus className="size-3.5" /> Stunde
      </Button>
      {!gueltig && <p role="alert" className="w-full text-[12px] text-warn">Das Ende muss nach dem Beginn liegen.</p>}
    </div>
  );
}
